import { z } from 'zod'

import { auditShape } from '@/shared/details'

export * from './paths'

const named = z.object({ id: z.string(), name: z.string() })

/** A record a plan names; its name is empty when it is gone. */
export type NamedRecord = z.infer<typeof named>

const summaryShape = {
  id: z.string(),
  name: z.string(),
  shortName: z.string().nullish(),
  organizationId: z.string(),
  organizationName: z.string(),
  isEnabled: z.boolean(),
  onDemandAccess: z.boolean(),
  availableToAll: z.boolean(),
  durationDays: z.number(),
  price: z.number().nullish(),
  courseCount: z.number(),
}

const summarySchema = z.object(summaryShape)

/** A plan as Find Subscription Profiles and the chooser list it. */
export type PlanSummary = z.infer<typeof summarySchema>

export function parsePlanSummary(item: unknown): PlanSummary {
  return summarySchema.parse(item)
}

const planSchema = z
  .object({
    ...summaryShape,
    description: z.string().nullish(),
    htmlDescription: z.string().nullish(),
    keywords: z.string().nullish(),
    imageUrl: z.string().nullish(),
    externalId: z.string().nullish(),
    internalPrice: z.number().nullish(),
    isBillable: z.boolean().nullish(),
    showsPricing: z.boolean(),
    enforcePrerequisites: z.boolean(),
    protectEndDate: z.boolean(),
    courses: z.array(named),
    learningPaths: z.array(named),
    contentLibraries: z.array(named),
    courseTagGroups: z.array(named),
    organizations: z.array(named),
    tags: z.array(named),
  })
  .extend(auditShape)

/** A plan as Subscription Profile Details and its edit form show it. */
export type Plan = z.infer<typeof planSchema>

export function parsePlan(body: unknown): Plan {
  return planSchema.parse(body)
}

const defaultsSchema = z.object({ durationDays: z.number(), onDemandAccess: z.boolean(), isBillable: z.boolean() })

/** What a new plan of an organization starts with. */
export type PlanDefaults = z.infer<typeof defaultsSchema>

export function parsePlanDefaults(body: unknown): PlanDefaults {
  return defaultsSchema.parse(body)
}

const coursePlanSchema = z.object({ id: z.string(), name: z.string(), mayChange: z.boolean() })

/** A plan holding a course, as the course form's tab lists it. */
export type CoursePlan = z.infer<typeof coursePlanSchema>

export function parseCoursePlan(item: unknown): CoursePlan {
  return coursePlanSchema.parse(item)
}
